import React, { useState, useEffect } from 'react';
import { Paper, Grid, Button, Theme } from '@quillbot/design-system';
import { makeStyles } from '@mui/styles';
import { copyTextToClipboard } from 'core/utils';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { RootState } from 'src/_common/models/redux/store';
import { Dims, CHAR_LIMITS } from 'config/constants';
import CharacterSummaryWarning from 'src/_common/components/label/Warning';
import { toggleKeyboardAction, setParaphraserDataAction } from '@Common/actions/commonActions';
import InsertDocButton from 'src/summarizer/components/shared/InsertToDoc';
// import FileExporter from 'src/_common/components/fileExporter/FileExporter';
import dynamic from 'next/dynamic';
import CONSTANTS from 'src/paraphraser/constants';
import FileUploader from 'src/_common/components/fileUploader/FileUploader';
// import VerticalSeparater from 'src/_common/quillElements/verticalSeparater';
// import YoutubeBtn from 'src/_common/components/YoutubeBtn';
import { openToastr } from '@Common/handlers/services';
import LoadBar from '../../_common/quillElements/spinner/LoadBar';
import QuillBtn from './shared/QuillBtn';
import SentenceSummary from './shared/SentenceSummary';
import CopyAllBtn from './shared/CopyAllBtn';
import { getOutputKey } from '../handlers/logic';
import { OutputAbs, OutputExt } from '../models/store';
import { copyAllText, quillItAction, paraphraseSummaryAction } from '../actions/controlActions';
// import { characterLimitExceededAction } from '../actions/systemActions';
import { ExtUnionGuard, AbsUnionGuard } from '../guards/union';
import CharacterSummary from './shared/CharacterSummary';
import TopicsDrawer from './TopicsDrawer';
import { userPastedText } from '../actions/systemActions';

const FileExporter = dynamic(() => import('src/_common/components/fileExporter/FileExporter'), {
  ssr: false,
});

const useStyles = makeStyles((theme: Theme) => ({
  controlsStyle: {
    // borderTop: '1px solid #E5E5E5',
    position: 'relative',
    // borderBottom: '1px solid #E5E5E5',
  },
  controlsStyleMobile: {
    borderTop: `1px ${theme.palette.divider} solid`,
    position: 'relative',
    borderBottom: `1px ${theme.palette.divider} solid`,
  },
  btnOutline: {
    // border: '1px solid',
    // borderColor: theme.palette.primary.main,
    borderRadius: '100px',
    fontWeight: 'bold',
    // '&:hover': {
    //   // background: '#EBF2ED',
    //   cursor: 'pointer',
    //   // borderColor: '#BAD6BF',
    // },
    [theme.breakpoints.down(769)]: {
      padding: '5px 8px',
    },
  },
  fileExporternBtn: {
    paddingLeft: '10px',
    [theme.breakpoints.down(769)]: {
      paddingLeft: '0px',
      marginRight: '-15px',
    },
  },
  copyBtn: {
    marginRight: '-10px',
    marginLeft: '5px',
  },
  exportBtn: {
    marginRight: '-10px',
  },
  insertBtn: {
    alignSelf: 'center',
  },
  rightControlsWrapper: {
    position: 'absolute',
    display: 'flex',
    right: '10px',
    top: '-1px',
  },
  hidden: {
    display: 'flex',
    justifyItems: 'center',
    alignItems: 'center',
    [theme.breakpoints.down(1000)]: {
      display: 'none',
    },
  },
}));
const extSliderValues = {
  min: 2,
  max: 20,
};
const absSliderValues = {
  min: 1,
  max: 4,
  marks: [{ value: 1 }, { value: 2 }, { value: 3 }, { value: 4 }],
};

interface IProps {
  getText: Function;
  setFileInfo: Function;
  droppedFile: { files: null | File[] };
  // setFileUploadArticleScrapperObject: Function;
  // fileUploadArticleScrapperObject: {
  //   fileFormatAllowed: boolean;
  //   isFileDragStarted: boolean;
  //   showLoadingSpinner: boolean;
  // };
  // setInputTextHandle: Function;
  // setIsYtBoxOpen: Function;
  // inputRef: HTMLInputElement;
}

export function InputControlsContainer(props: IProps) {
  const user = useSelector((state: RootState) => state.user);
  let currentWordLimit: number = CHAR_LIMITS.SUMMARIZER_WORD_LIMIT_GUEST;
  if (user && user.isAuthenticated) {
    currentWordLimit = CHAR_LIMITS.SUMMARIZER_WORD_LIMIT_MEMBER;
  }
  if (user && user.premium) {
    currentWordLimit = CHAR_LIMITS.SUMMARIZER_WORD_LIMIT_PREMIUM;
  }
  const classes = useStyles();
  const [isRequill, setRequill] = useState(false);

  const isQuilling = useSelector((state: RootState) => state.smzr.controls.isQuilling);
  const mode = useSelector((state: RootState) => state.smzr.controls.summaryMode);
  const isAbsSummaryExist = useSelector((state: RootState) => state.smzr.output.isAbsSummaryExist);
  const absLengthGauge = useSelector((state: RootState) => state.smzr.controls.absLengthGauge);
  const totalOutputSentences = useSelector((state: RootState) => {
    const outputKey = getOutputKey(state.smzr.controls.summaryMode, absLengthGauge);
    const isExtType = ExtUnionGuard(state.smzr.output[outputKey]);
    return isExtType ? (state.smzr.output[outputKey] as OutputExt).sentenceSequence.length : 0;
  });
  const inputWordLength = useSelector((state: RootState) => state.smzr.input.numberOfWords);

  const isOutputAvailable = useSelector((state: RootState) => {
    const outputKey = getOutputKey(mode, state.smzr.controls.absLengthGauge);
    const isAbsType = AbsUnionGuard(state.smzr.output[outputKey]);
    return isAbsType ? (state.smzr.output[outputKey] as OutputAbs).summaryPara : '';
  });

  const dispatch = useDispatch();

  useEffect(() => {
    if (mode === 'abs' && inputWordLength > 0) {
      if (isAbsSummaryExist && !isOutputAvailable && !isRequill) {
        setRequill(true);
      } else if (isOutputAvailable && isRequill) {
        setRequill(false);
      }
    } else if (mode === 'ext' && isRequill) {
      setRequill(false);
    }
  }, [mode, absLengthGauge, isQuilling]);

  if (totalOutputSentences) extSliderValues.max = totalOutputSentences;
  else extSliderValues.max = 20;

  return (
    <Paper elevation={0} className={classes.controlsStyle} style={{ borderRadius: '0 0  0 0px' }}>
      <TopicsDrawer />
      <Grid
        style={{
          height: `calc(${Dims.footer}px + 2px)`,
          // padding: '0px 12px 0px 0px',
        }}
        container
        alignItems="center"
        justifyContent="space-between"
      >
        <Grid item xs>
          {inputWordLength ? (
            <CharacterSummary value={inputWordLength} limit={currentWordLimit} />
          ) : (
            <div style={{ display: 'flex' }}>
              <FileUploader
                getText={props.getText}
                droppedFile={props.droppedFile}
                setFileInfo={props.setFileInfo}
              />
              {/* <span className={classes.hidden}>
                <VerticalSeparater height="13.05px" color="#828282" margin="0px 7.5px" />
              </span> */}
              {/* <YoutubeBtn
                setFileUploadArticleScrapperObject={props.setFileUploadArticleScrapperObject}
                fileUploadArticleScrapperObject={props.fileUploadArticleScrapperObject}
                setIsYtBoxOpen={props.setIsYtBoxOpen}
                getText={props.getText}
                setInputTextHandle={props.setInputTextHandle}
                placement="top-start"
                inputRef={props.inputRef}
              /> */}
            </div>
          )}
        </Grid>
        <Grid item style={{ position: 'relative' }}>
          <Grid
            container
            direction="column"
            alignItems="center"
            style={{ paddingRight: '12px', paddingLeft: '12px' }}
          >
            <Grid item container className="original-text-btns" justifyContent="flex-end">
              <QuillBtn
                inProgress={isQuilling}
                onClick={() => dispatch(quillItAction())}
                isRequill={isRequill}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Paper>
  );
}

const getModifiedText = () => {
  const outputBoxElem = document.getElementById('outputBoxSummarizer');
  let textToCopy = '';
  if (outputBoxElem) textToCopy = outputBoxElem.innerText;
  return textToCopy;
};

export function OutputControlsContainer() {
  const router = useRouter();
  const classes = useStyles();
  const [showCopyTextModal, setCopyModalState] = useState(false);
  const mode = useSelector((state: RootState) => state.smzr.controls.summaryMode);
  const isExtension = useSelector((state: RootState) => state.common.platform.isExtension);
  const isIE = useSelector((state: RootState) => state.common.platform.isIE);

  // const isAbsSummaryExist = useSelector((state: RootState) => state.smzr.output.isAbsSummaryExist);
  const numberOfSentences = useSelector((state: RootState) => {
    const outputKey = getOutputKey(mode, state.smzr.controls.absLengthGauge);
    const isExtType = ExtUnionGuard(state.smzr.output[outputKey]);
    const isAbsType = AbsUnionGuard(state.smzr.output[outputKey]);

    return isExtType || isAbsType
      ? (state.smzr.output[outputKey] as OutputExt | OutputAbs).numberOfSentences
      : 0;
  });
  const isQuilling = useSelector((state: RootState) => state.smzr.controls.isQuilling);
  const absSummary = useSelector((state: RootState) => {
    const outputKey = getOutputKey(mode, state.smzr.controls.absLengthGauge);
    const isAbsType = AbsUnionGuard(state.smzr.output[outputKey]);
    return isAbsType ? (state.smzr.output[outputKey] as OutputAbs).summaryPara : '';
  });

  const dispatch = useDispatch();

  const copyText = (triggeredBy = 'copyAll') => {
    const textToCopy = getModifiedText();
    // copyTextToClipboard(textToCopy)
    try {
      copyTextToClipboard(
        textToCopy,
        () => {
          dispatch(copyAllText(textToCopy, triggeredBy));
        },
        () => null
      );
    } catch (err) {
      // dispatch({type: });
      setCopyModalState(true);
    }
  };

  const paraphraseSummary = () => {
    const editedSummary = getModifiedText();
    dispatch(
      paraphraseSummaryAction({
        summary: editedSummary,
      })
    );
    copyText('paraphraseSummary');
    dispatch(setParaphraserDataAction(editedSummary));

    router.push('/');
  };

  const handleHotkeyEvents = (event: KeyboardEvent) => {
    if (event.key === 'c' && (event.ctrlKey || event.metaKey)) {
      // Do not apply hot key if something is selected in window
      const selection = window.getSelection();
      if (selection) {
        if (selection.toString()) return;
      }
      const textToCopy = getModifiedText();
      if (!textToCopy?.length) return;
      copyText('hotkey');
      openToastr('Text copied!');
      event.stopPropagation();
    }
    if (event.key === 'v' && (event.ctrlKey || event.metaKey)) {
      dispatch(userPastedText('hotkey'));
    }

    if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
      dispatch(quillItAction());
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleHotkeyEvents);
    return () => document.removeEventListener('keydown', handleHotkeyEvents);
  }, []);

  return (
    <Paper elevation={0} className={classes.controlsStyle} style={{ borderRadius: '0 0 0px 0' }}>
      {CONSTANTS.isProgressBarOn && <LoadBar loading={isQuilling} />}
      <Grid
        container
        style={{
          height: `calc(${Dims.footer}px + 2px)`,
          padding: '3px 0 3px 18px',
        }}
        justifyContent="space-between"
        alignItems="center"
      >
        <Grid item style={{ fontWeight: 600 }}>
          <SentenceSummary sentenceCount={numberOfSentences} />
        </Grid>

        <Grid item>
          <Grid container alignItems="center" justifyContent="space-between">
            {numberOfSentences > 0 && (
              <Button
                color="primary"
                variant="outlined"
                disableRipple
                onClick={paraphraseSummary}
                classes={{ outlinedPrimary: classes.btnOutline }}
              >
                Paraphrase Summary
              </Button>
            )}
            <Grid item className={classes.fileExporternBtn}>
              {!isIE && numberOfSentences > 0 && !isExtension && (
                <FileExporter
                  getText={getModifiedText}
                  copyAllEvent={() => copyText('exportButton')}
                />
              )}
            </Grid>
            {numberOfSentences > 0 && (
              <CopyAllBtn
                onClick={() => {
                  copyText();
                  openToastr('Text copied!');
                }}
                showModal={showCopyTextModal}
                onModalClose={() => setCopyModalState(false)}
              />
            )}
          </Grid>
        </Grid>
      </Grid>
    </Paper>
  );
}

export function MobileControlsContainer() {
  const user = useSelector((state: RootState) => state.user);
  let currentWordLimit: number = CHAR_LIMITS.SUMMARIZER_WORD_LIMIT_GUEST;
  if (user && user.isAuthenticated) {
    currentWordLimit = CHAR_LIMITS.SUMMARIZER_WORD_LIMIT_MEMBER;
  }
  if (user && user.premium) {
    currentWordLimit = CHAR_LIMITS.SUMMARIZER_WORD_LIMIT_PREMIUM;
  }
  const [isRequill, setRequill] = useState(false);
  const classes = useStyles();
  const [showCopyTextModal, setCopyModalState] = useState(false);
  const absLengthGauge = useSelector((state: RootState) => state.smzr.controls.absLengthGauge);
  const totalOutputSentences = useSelector((state: RootState) => {
    const outputKey = getOutputKey(state.smzr.controls.summaryMode, absLengthGauge);
    const isExtType = ExtUnionGuard(state.smzr.output[outputKey]);
    return isExtType ? (state.smzr.output[outputKey] as OutputExt).sentenceSequence.length : 0;
  });
  const activeTab = useSelector((state: RootState) => state.smzr.controls.activeTab);
  const mode = useSelector((state: RootState) => state.smzr.controls.summaryMode);
  const isExtension = useSelector((state: RootState) => state.common.platform.isExtension);
  const isMobile = useSelector((state: RootState) => state.common.platform.isMobile);

  const isIE = useSelector((state: RootState) => state.common.platform.isIE);
  const native = useSelector((state: RootState) => state.common.platform.native);
  const isQuilling = useSelector((state: RootState) => state.smzr.controls.isQuilling);
  const inputWordLength = useSelector((state: RootState) => state.smzr.input.numberOfWords);
  const isAbsSummaryExist = useSelector((state: RootState) => state.smzr.output.isAbsSummaryExist);
  const isOutputAvailable = useSelector((state: RootState) => {
    const outputKey = getOutputKey(mode, state.smzr.controls.absLengthGauge);
    const isAbsType = AbsUnionGuard(state.smzr.output[outputKey]);
    return isAbsType ? (state.smzr.output[outputKey] as OutputAbs).summaryPara : '';
  });
  const extensionName = useSelector(
    (state: RootState) => state.common.platform.extensionMetaData?.type
  );
  const dispatch = useDispatch();

  const copyText = (triggeredBy = 'copyAll') => {
    const outputBoxElem = document.getElementById('outputBoxSummarizer');
    let textToCopy = '';
    if (outputBoxElem) textToCopy = outputBoxElem.innerText;
    // copyTextToClipboard(textToCopy)
    try {
      copyTextToClipboard(
        textToCopy,
        () => {
          dispatch(copyAllText(textToCopy, triggeredBy));
        },
        () => null
      );
    } catch (err) {
      // dispatch({type: });
      setCopyModalState(true);
    }
  };
  let showCopyAll = false;
  // as default mode is abstractive
  let sliderValues = absSliderValues;
  const isSummarizedTab = activeTab === 'summarized';
  if (activeTab === 'summarized') {
    showCopyAll = true;
    if (mode === 'ext') {
      sliderValues = { ...sliderValues, ...extSliderValues };
      if (totalOutputSentences) sliderValues.max = totalOutputSentences;
      else sliderValues.max = 20;
    }
  }

  useEffect(() => {
    if (mode === 'abs' && inputWordLength > 0) {
      if (isAbsSummaryExist && !isOutputAvailable && !isRequill) {
        setRequill(true);
      } else if (isOutputAvailable && isRequill) {
        setRequill(false);
      }
    } else if (mode === 'ext' && isRequill) {
      setRequill(false);
    }
  }, [mode, absLengthGauge, isQuilling]);

  const onSummarizeClick = () => {
    dispatch(quillItAction());
    dispatch(toggleKeyboardAction(false));
  };

  return (
    <Paper elevation={0} className={classes.controlsStyleMobile}>
      <TopicsDrawer />
      {!isSummarizedTab && (
        <CharacterSummaryWarning
          value={inputWordLength}
          limit={currentWordLimit}
          referrer="summarizerWordLimit"
          native={native}
        />
      )}
      <Grid
        container
        alignItems="center"
        justifyContent="space-between"
        style={{
          // paddingLeft: '12px',
          // paddingRight: '12px',
          minHeight: '48px',
          position: 'relative',
        }}
      >
        <Grid item xs container>
          {/* show insert button only in ms-word and google docs extension */}
          {isExtension && extensionName !== 'chrome' && isSummarizedTab ? (
            <Grid item xs className={classes.insertBtn}>
              <InsertDocButton />
            </Grid>
          ) : null}
          {!isSummarizedTab && (
            <Grid item xs container alignItems="center">
              <CharacterSummary value={inputWordLength} limit={currentWordLimit} />
            </Grid>
          )}

          <Grid
            item
            xs
            container
            alignItems="center"
            justifyContent={isSummarizedTab ? 'center' : 'flex-end'}
            style={{ marginRight: isExtension || isMobile ? '10px' : undefined }}
          >
            <QuillBtn
              inProgress={isQuilling}
              onClick={() => onSummarizeClick()}
              isRequill={isRequill}
            />
          </Grid>
          <Grid className={`${!isExtension ? classes.rightControlsWrapper : null}`}>
            {!isIE && showCopyAll && !isExtension && (
              <div className={classes.exportBtn}>
                <FileExporter
                  getText={getModifiedText}
                  copyAllEvent={() => copyText('exportButton')}
                />
              </div>
            )}
            {showCopyAll && (
              <div className={`${isExtension ? undefined : classes.copyBtn}`}>
                <CopyAllBtn
                  onClick={() => {
                    copyText();
                    openToastr('Text copied!');
                  }}
                  showModal={showCopyTextModal}
                  onModalClose={() => setCopyModalState(false)}
                />
              </div>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Paper>
  );
}
